import type { FoundryV2CompositionDraftRecord } from "../api/foundryV2Client";

type Props = {
  compositionDraft: FoundryV2CompositionDraftRecord | null;
  isLoading: boolean;
};

function sectionTone(selection: "required" | "recommended" | "optional" | "rejected"): string {
  switch (selection) {
    case "required":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "recommended":
      return "border-blue-200 bg-blue-50 text-blue-700";
    case "optional":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border-rose-200 bg-rose-50 text-rose-700";
  }
}

function bucketTitle(selection: "required" | "recommended" | "optional" | "rejected"): string {
  switch (selection) {
    case "required":
      return "Required for first delivery";
    case "recommended":
      return "Recommended by Foundry";
    case "optional":
      return "Compatible but deferred";
    default:
      return "Rejected for this contract";
  }
}

export function FoundryBrickSelectionPanel({ compositionDraft, isLoading }: Props): JSX.Element | null {
  if (!compositionDraft && !isLoading) return null;

  const selection = compositionDraft?.foundryContract.brickSelection;
  const entries = selection?.entries ?? [];
  const buckets: Array<"required" | "recommended" | "optional" | "rejected"> = [
    "required",
    "recommended",
    "optional",
    "rejected",
  ];

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_52%,#f2f7ff_100%)] p-5 shadow-[0_18px_50px_rgba(15,23,42,0.08)]"
      data-testid="foundry-brick-selection-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Foundry catalog wiring</div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">Frozen brick selection before Jarvis executes</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Foundry now locks which bricks are required, recommended, optional, or rejected. Jarvis should assemble the selected set, not rediscover it during delivery.
          </p>
        </div>
        {selection ? (
          <div className="rounded-2xl border border-white/70 bg-white/85 p-4 text-sm text-slate-700 shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Selection posture</div>
            <div className="mt-2 font-semibold text-slate-900">{selection.status}</div>
            <div className="mt-2 grid gap-2 text-xs text-slate-600">
              <div>{selection.requiredBrickIds.length} required</div>
              <div>{selection.recommendedBrickIds.length} recommended</div>
              <div>{selection.optionalBrickIds.length} optional</div>
              <div>{selection.rejectedBrickIds.length} rejected</div>
            </div>
          </div>
        ) : null}
      </div>

      {selection ? (
        <>
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white/90 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Why this selection is frozen</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {selection.rationale.map((item) => (
                <li key={item} className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2">
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-2">
            {buckets.map((bucket) => {
              const bucketEntries = entries.filter((entry) => entry.selection === bucket);
              return (
                <div key={bucket} className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${sectionTone(bucket)}`}>
                      {bucket}
                    </span>
                    <div className="text-sm font-semibold text-slate-900">{bucketTitle(bucket)}</div>
                  </div>
                  <div className="mt-2 text-xs leading-5 text-slate-600">
                    {bucket === "required"
                      ? "These bricks are mandatory to keep the service map, governance posture, and first proof run coherent."
                      : bucket === "recommended"
                        ? "These bricks preserve the archetype and operator experience without widening the frozen scope unnecessarily."
                        : bucket === "optional"
                          ? "These bricks stay compatible for later iterations or a deeper custom delta."
                          : "These bricks are deliberately kept out of the first contract to avoid scope drift or archetype mismatch."}
                  </div>
                  {bucketEntries.length ? (
                    <div className="mt-3 grid gap-3">
                      {bucketEntries.map((entry) => (
                        <article key={`${bucket}-${entry.brickId}`} className="rounded-2xl border border-slate-100 bg-slate-50/90 p-4">
                          <div className="flex flex-wrap items-center gap-2">
                          <div className="text-sm font-semibold text-slate-900">{entry.title}</div>
                          <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                            {entry.brickId}
                          </span>
                          <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                            {entry.family}
                          </span>
                            <span
                              className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                entry.sensitive ? "border-rose-200 bg-rose-50 text-rose-700" : "border-slate-200 bg-white text-slate-600"
                              }`}
                            >
                              {entry.sensitive ? "sensitive" : "standard"}
                            </span>
                          </div>
                          <div className="mt-2 text-sm text-slate-700">{entry.summary}</div>
                          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500">
                            <span>Source</span>
                            <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                              {entry.source.replace(/-/g, " ")}
                            </span>
                          </div>
                          <ul className="mt-3 space-y-2 text-xs text-slate-600">
                            {entry.rationale.map((item) => (
                              <li key={item} className="rounded-lg border border-slate-100 bg-white px-3 py-2">
                                {item}
                              </li>
                            ))}
                          </ul>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500">
                      No bricks are currently marked as {bucket}.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          {isLoading ? "Locking brick selection..." : "Generate a Foundry contract v2 to freeze the brick selection."}
        </div>
      )}
    </section>
  );
}
